/**
 * Air Quality API Service
 * Fetches real-time air quality data from multiple free APIs
 *
 * Sources:
 * - OpenAQ (free, no API key)
 * - World Air Quality Index (WAQI) - free token
 * - Open-Meteo (weather data, free)
 */

import type { AirQualityData, PollutantReading, AirQualityLevel, AirQualityForecast } from '@/types';

// WAQI API token — get a free token at https://aqicn.org/data-platform/token/
// Add as VITE_WAQI_TOKEN in Vercel environment variables
const WAQI_TOKEN = import.meta.env.VITE_WAQI_TOKEN || 'demo';

// API Endpoints
const OPENAQ_BASE = 'https://api.openaq.org/v2';
const WAQI_BASE = 'https://api.waqi.info';
const OPEN_METEO_BASE = 'https://api.open-meteo.com/v1';

// Types for API responses
interface OpenAQMeasurement {
  parameter: string;
  value: number;
  unit: string;
  lastUpdated: string;
}

interface WAQIResponse {
  status: string;
  data: {
    aqi: number;
    idx: number;
    attributions: { name: string; url: string }[];
    city: { name: string; geo: [number, number] };
    dominentpol: string;
    iaqi: {
      pm25?: { v: number };
      pm10?: { v: number };
      o3?: { v: number };
      no2?: { v: number };
      so2?: { v: number };
      co?: { v: number };
      t?: { v: number };
      h?: { v: number };
      p?: { v: number };
      w?: { v: number };
    };
    time: { s: string; tz: string; v: number; iso: string };
    forecast?: {
      daily: {
        pm25?: { day: string; avg: number; min: number; max: number }[];
        pm10?: { day: string; avg: number; min: number; max: number }[];
        o3?: { day: string; avg: number; min: number; max: number }[];
      };
    };
  };
}

interface WeatherData {
  temperature: number;
  humidity: number;
  windSpeed: number;
  pressure: number;
  uvIndex: number;
}

/**
 * Determine air quality level from AQI value
 */
function getAQILevel(aqi: number): AirQualityLevel {
  if (aqi <= 50) return 'good';
  if (aqi <= 100) return 'moderate';
  if (aqi <= 150) return 'unhealthy-sensitive';
  if (aqi <= 200) return 'unhealthy';
  if (aqi <= 300) return 'very-unhealthy';
  return 'hazardous';
}

/**
 * Create a pollutant reading object
 */
function createPollutantReading(
  value: number,
  unit: string,
  previousValue?: number
): PollutantReading {
  const trend = previousValue
    ? value > previousValue * 1.05
      ? 'rising'
      : value < previousValue * 0.95
        ? 'falling'
        : 'stable'
    : 'stable';

  // Determine status based on pollutant type and value
  let status: AirQualityLevel = 'good';
  if (unit === 'µg/m³') {
    // PM2.5 thresholds
    if (value <= 12) status = 'good';
    else if (value <= 35.4) status = 'moderate';
    else if (value <= 55.4) status = 'unhealthy-sensitive';
    else if (value <= 150.4) status = 'unhealthy';
    else if (value <= 250.4) status = 'very-unhealthy';
    else status = 'hazardous';
  } else if (unit === 'ppb') {
    // Ozone thresholds
    if (value <= 54) status = 'good';
    else if (value <= 70) status = 'moderate';
    else if (value <= 85) status = 'unhealthy-sensitive';
    else if (value <= 105) status = 'unhealthy';
    else if (value <= 200) status = 'very-unhealthy';
    else status = 'hazardous';
  } else if (unit === 'ppm') {
    // CO2 thresholds
    if (value <= 600) status = 'good';
    else if (value <= 1000) status = 'moderate';
    else if (value <= 1500) status = 'unhealthy-sensitive';
    else if (value <= 2000) status = 'unhealthy';
    else status = 'very-unhealthy';
  }

  return {
    value,
    unit,
    status,
    trend,
    timestamp: Date.now(),
  };
}

/**
 * Fetch air quality from WAQI API (World Air Quality Index)
 * This is the primary source - most comprehensive
 */
export async function fetchWAQIData(lat: number, lng: number): Promise<WAQIResponse | null> {
  try {
    const url = `${WAQI_BASE}/feed/geo:${lat};${lng}/?token=${WAQI_TOKEN}`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`WAQI API error: ${response.status}`);
    }

    const data: WAQIResponse = await response.json();

    if (data.status !== 'ok') {
      throw new Error('WAQI returned error status');
    }

    return data;
  } catch (error) {
    console.error('WAQI fetch error:', error);
    return null;
  }
}

/**
 * Fetch weather data from Open-Meteo (free, no API key)
 */
export async function fetchWeatherData(lat: number, lng: number): Promise<WeatherData | null> {
  try {
    const url = `${OPEN_METEO_BASE}/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,surface_pressure,wind_speed_10m,uv_index`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }

    const data = await response.json();

    return {
      temperature: data.current.temperature_2m,
      humidity: data.current.relative_humidity_2m,
      windSpeed: data.current.wind_speed_10m,
      pressure: data.current.surface_pressure,
      uvIndex: data.current.uv_index ?? 0,
    };
  } catch (error) {
    console.error('Weather fetch error:', error);
    return null;
  }
}

/**
 * Fetch from OpenAQ API (backup source)
 */
export async function fetchOpenAQData(lat: number, lng: number, radius: number = 25000): Promise<OpenAQMeasurement[] | null> {
  try {
    const url = `${OPENAQ_BASE}/latest?coordinates=${lat},${lng}&radius=${radius}&limit=100`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`OpenAQ API error: ${response.status}`);
    }

    const data = await response.json();

    // Aggregate measurements from nearby stations
    const measurements: OpenAQMeasurement[] = [];

    for (const result of data.results || []) {
      for (const measurement of result.measurements || []) {
        measurements.push({
          parameter: measurement.parameter,
          value: measurement.value,
          unit: measurement.unit,
          lastUpdated: measurement.lastUpdated,
        });
      }
    }

    return measurements;
  } catch (error) {
    console.error('OpenAQ fetch error:', error);
    return null;
  }
}

/**
 * Atmospheric Latency Factor calculation
 * How long pollutants "hang" in the air based on weather
 */
export function calculateAtmosphericLatencyFactor(weather: WeatherData): number {
  // Higher humidity = pollutants stay longer
  const humidityFactor = weather.humidity / 100;

  // Low wind = pollutants accumulate
  const windFactor = Math.max(0.1, 1 - (weather.windSpeed / 30));

  // Temperature inversion effect (cold air traps pollutants)
  const tempFactor = weather.temperature < 10 ? 1.2 : weather.temperature > 30 ? 0.9 : 1;

  // Combined factor (1.0 = normal, >1 = worse, <1 = better dispersion)
  return humidityFactor * 0.3 + windFactor * 0.5 + tempFactor * 0.2;
}

/**
 * Main function: Fetch comprehensive air quality data
 */
export async function fetchAirQuality(lat: number, lng: number): Promise<AirQualityData | null> {
  // Fetch from multiple sources in parallel
  const [waqiData, weatherData] = await Promise.all([
    fetchWAQIData(lat, lng),
    fetchWeatherData(lat, lng),
  ]);

  if (!waqiData) {
    // Try OpenAQ as backup
    const openAQData = await fetchOpenAQData(lat, lng);
    if (!openAQData || openAQData.length === 0) {
      return null;
    }

    // Build data from OpenAQ (less comprehensive)
    const pm25Value = openAQData.find(m => m.parameter === 'pm25')?.value ?? 15;
    const pm10Value = openAQData.find(m => m.parameter === 'pm10')?.value ?? 25;
    const o3Value = openAQData.find(m => m.parameter === 'o3')?.value ?? 30;
    const no2Value = openAQData.find(m => m.parameter === 'no2')?.value ?? 20;

    // Estimate AQI from PM2.5
    const estimatedAQI = pm25Value <= 12 ? pm25Value * 4.17
      : pm25Value <= 35.4 ? 50 + (pm25Value - 12) * 2.13
      : pm25Value <= 55.4 ? 100 + (pm25Value - 35.4) * 2.5
      : pm25Value <= 150.4 ? 150 + (pm25Value - 55.4) * 0.53
      : 200 + (pm25Value - 150.4) * 0.5;

    return {
      pm25: createPollutantReading(pm25Value, 'µg/m³'),
      pm10: createPollutantReading(pm10Value, 'µg/m³'),
      ozone: createPollutantReading(o3Value, 'ppb'),
      co2: createPollutantReading(450, 'ppm'), // OpenAQ doesn't have CO2, use outdoor average
      no2: createPollutantReading(no2Value, 'ppb'),
      aqi: Math.round(estimatedAQI),
      dominantPollutant: 'pm25',
      timestamp: Date.now(),
      location: { lat, lng },
    };
  }

  // Build comprehensive data from WAQI
  const iaqi = waqiData.data.iaqi;

  // Apply atmospheric latency factor if we have weather data
  const latencyFactor = weatherData ? calculateAtmosphericLatencyFactor(weatherData) : 1;

  // PM2.5 with latency adjustment
  const pm25Raw = iaqi.pm25?.v ?? 15;
  const pm25Adjusted = pm25Raw * (0.8 + latencyFactor * 0.4); // Adjust based on atmospheric conditions

  // PM10
  const pm10Raw = iaqi.pm10?.v ?? 25;

  // Ozone (O3) - affected by temperature and UV
  const ozoneRaw = iaqi.o3?.v ?? 30;
  const ozoneAdjusted = weatherData && weatherData.temperature > 25
    ? ozoneRaw * 1.1 // Ozone increases with heat
    : ozoneRaw;

  // NO2 - traffic indicator
  const no2Raw = iaqi.no2?.v ?? 20;

  // CO2 - WAQI doesn't have this, estimate based on location type
  // Urban areas have higher CO2
  const co2Estimated = 420 + (waqiData.data.aqi / 2); // Baseline + AQI correlation

  return {
    pm25: createPollutantReading(pm25Adjusted, 'µg/m³'),
    pm10: createPollutantReading(pm10Raw, 'µg/m³'),
    ozone: createPollutantReading(ozoneAdjusted, 'ppb'),
    co2: createPollutantReading(co2Estimated, 'ppm'),
    no2: createPollutantReading(no2Raw, 'ppb'),
    aqi: waqiData.data.aqi,
    dominantPollutant: waqiData.data.dominentpol || 'pm25',
    timestamp: Date.now(),
    location: { lat, lng },
  };
}

/**
 * Fetch air quality forecast
 */
export async function fetchAirQualityForecast(lat: number, lng: number): Promise<AirQualityForecast[]> {
  const waqiData = await fetchWAQIData(lat, lng);

  if (!waqiData?.data.forecast?.daily) {
    // Return simulated forecast if API doesn't provide one
    return generateSimulatedForecast();
  }

  const forecast: AirQualityForecast[] = [];
  const pm25Forecast = waqiData.data.forecast.daily.pm25 || [];
  const o3Forecast = waqiData.data.forecast.daily.o3 || [];

  for (let i = 0; i < Math.min(pm25Forecast.length, 5); i++) {
    const pm25 = pm25Forecast[i];
    const o3 = o3Forecast[i];

    // Estimate AQI from PM2.5 average
    const estimatedAQI = pm25.avg <= 12 ? pm25.avg * 4.17
      : pm25.avg <= 35.4 ? 50 + (pm25.avg - 12) * 2.13
      : 100 + (pm25.avg - 35.4) * 2.5;

    forecast.push({
      time: new Date(pm25.day).getTime(),
      aqi: Math.round(estimatedAQI),
      pm25: pm25.avg,
      ozone: o3?.avg ?? 30,
      status: getAQILevel(Math.round(estimatedAQI)),
    });
  }

  return forecast;
}

/**
 * Generate simulated forecast when API doesn't provide one
 */
function generateSimulatedForecast(): AirQualityForecast[] {
  const forecast: AirQualityForecast[] = [];
  const now = Date.now();

  for (let i = 0; i < 24; i++) {
    // Simulate daily pollution pattern
    // Higher in morning (7-9am) and evening (5-7pm) rush hours
    const hour = new Date(now + i * 60 * 60 * 1000).getHours();
    let baseAQI = 45;

    if ((hour >= 7 && hour <= 9) || (hour >= 17 && hour <= 19)) {
      baseAQI = 65 + Math.random() * 20; // Rush hour peak
    } else if (hour >= 12 && hour <= 14) {
      baseAQI = 55 + Math.random() * 15; // Midday moderate
    } else if (hour >= 2 && hour <= 5) {
      baseAQI = 30 + Math.random() * 10; // Night lowest
    }

    const pm25 = baseAQI * 0.3;

    forecast.push({
      time: now + i * 60 * 60 * 1000,
      aqi: Math.round(baseAQI),
      pm25: Math.round(pm25 * 10) / 10,
      ozone: Math.round(25 + Math.random() * 15),
      status: getAQILevel(Math.round(baseAQI)),
    });
  }

  return forecast;
}

/**
 * Fetch air quality for nearby grid points (for heatmap)
 */
export async function fetchAirQualityGrid(
  centerLat: number,
  centerLng: number,
  radiusKm: number = 5,
  gridSize: number = 5
): Promise<{ lat: number; lng: number; aqi: number; ni: number }[]> {
  const points: { lat: number; lng: number; aqi: number; ni: number }[] = [];

  // Create grid of points
  const step = (radiusKm * 2) / gridSize;
  const latStep = step / 111; // Approx km per degree latitude
  const lngStep = step / (111 * Math.cos(centerLat * Math.PI / 180)); // Adjust for longitude

  // For demo, simulate variation based on distance from center
  // In production, you'd make API calls for each point
  for (let i = 0; i < gridSize; i++) {
    for (let j = 0; j < gridSize; j++) {
      const lat = centerLat - (radiusKm / 111) + (i * latStep);
      const lng = centerLng - (radiusKm / (111 * Math.cos(centerLat * Math.PI / 180))) + (j * lngStep);

      // Simulate variation - industrial areas, parks, etc.
      const distFromCenter = Math.sqrt(
        Math.pow((i - gridSize / 2) / gridSize, 2) +
        Math.pow((j - gridSize / 2) / gridSize, 2)
      );

      // Random variation with some spatial correlation
      const baseAQI = 50 + Math.random() * 30;
      const aqi = Math.round(baseAQI + distFromCenter * 20);

      // Calculate Neural Index
      const pm25Estimated = aqi * 0.3;
      const ni = Math.round(100 - (pm25Estimated / 12) * 10 - (aqi - 50) * 0.3);

      points.push({
        lat,
        lng,
        aqi: Math.min(300, Math.max(0, aqi)),
        ni: Math.min(100, Math.max(0, ni)),
      });
    }
  }

  return points;
}

export type { WeatherData };
